import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function ElectricalWork() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/35cf116e20a7ae53ee49c2f34f4a5485.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Electrical Work Training
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto">
            Become An Electrician - Power Your Future Career
          </p>
          <Button href="/contact" size="lg">
            Enroll Now
          </Button>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-6">Program Overview</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Join our journeyman electrician program to gain practical skills and theoretical knowledge essential for a successful career in the electrical industry. Learn from industry experts through our comprehensive online platform.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our comprehensive training prepares you for certification exams and equips you with the expertise needed to work safely and efficiently in residential, commercial, and industrial electrical settings.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20electrical%20technician%20working%20in%20modern%20data%20center%20server%20room%20with%20rows%20of%20network%20equipment%20and%20cable%20management%20systems%20wearing%20safety%20equipment%20checking%20electrical%20connections%20and%20power%20distribution%20units%20among%20server%20racks%20with%20LED%20indicator%20lights%20and%20organized%20cabling%20infrastructure%20bright%20fluorescent%20lighting%20clean%20technical%20environment%20realistic%20detailed%20photography&width=800&height=600&seq=electrical-datacenter-tech-work-004&orientation=landscape"
                alt="Electrical Training"
                className="rounded-lg shadow-xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">What You'll Learn</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Electrical Theory",
                description: "Master fundamental electrical concepts including voltage, current, resistance, and power calculations."
              },
              {
                title: "Wiring & Installation",
                description: "Learn proper techniques for installing electrical wiring, outlets, switches, and lighting fixtures."
              },
              {
                title: "Circuit Design",
                description: "Understand circuit design principles, load calculations, and proper circuit protection methods."
              },
              {
                title: "National Electric Code",
                description: "Study NEC requirements, code compliance, and proper installation standards for all electrical work."
              },
              {
                title: "Safety Procedures",
                description: "Master electrical safety protocols, lockout/tagout procedures, and personal protective equipment usage."
              },
              {
                title: "Troubleshooting",
                description: "Develop diagnostic skills to identify and resolve electrical problems efficiently and safely."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-check-line text-2xl text-blue-900"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Career Opportunities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              "Journeyman Electrician",
              "Residential Electrician",
              "Commercial Electrician",
              "Industrial Electrician",
              "Maintenance Electrician",
              "Electrical Contractor"
            ].map((career, index) => (
              <div key={index} className="flex items-center bg-gray-50 p-6 rounded-lg">
                <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-briefcase-line text-xl text-blue-900"></i>
                </div>
                <span className="text-lg font-semibold text-black">{career}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Details */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Program Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Flexible Training</h3>
              <p className="text-gray-700">Online theory and virtual training sessions that fit your schedule</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-medal-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Certification</h3>
              <p className="text-gray-700">Covering everything from basic electrical principles to advanced troubleshooting and maintenance techniques</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Expert Instruction</h3>
              <p className="text-gray-700">Learn from experienced electricians with real-world industry knowledge</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Electrical Career?</h2>
          <p className="text-xl text-white mb-8">
            Join our program today and become a certified electrical professional.
          </p>
          <Button href="/contact" size="lg">
            Contact Us Today
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
